export interface RGB {
  r: number;
  g: number;
  b: number;
}

export function isValidRgbValue(value: number) {
  return typeof value === "number" && value >= 0 && value <= 255;
}

export function rgbToHex(r: number, g: number, b: number) {
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;
}

export function hexToRgb(hex: string): RGB | null {
  hex = hex.replace(/^#/, "");

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (hex.length !== 6 || !/^[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return { r, g, b };
}

export const isRgbObject = (value: unknown): value is RGB => {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { r: unknown }).r === "number" &&
    typeof (value as { g: unknown }).g === "number" &&
    typeof (value as { b: unknown }).b === "number"
  );
};

interface HSL {
  h: number;
  s: number;
  l: number;
}

function hexToHsl(hex: string): HSL {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): RGB {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

export function generateSimplePalette(baseHex: string) {
  const baseHsl = hexToHsl(baseHex);
  const palette: Record<string, string> = {};

  const steps = [
    { key: "m4", sMult: 1.127, lVal: baseHsl.l - 0.136 },
    { key: "m3", sMult: 1.0, lVal: baseHsl.l + 0.0 },
    { key: "m2", sMult: 0.915, lVal: baseHsl.l + 0.106 },
    { key: "m1", sMult: 0.853, lVal: baseHsl.l + 0.149 },
    { key: "0", sMult: 0.822, lVal: baseHsl.l + 0.18 },
    { key: "p1", sMult: 1, lVal: 0.62 },
    { key: "p2", sMult: 1, lVal: 0.73 },
    { key: "p3", sMult: 1, lVal: 0.85 },
    { key: "p4", sMult: 1, lVal: 0.9 },
    { key: "p5", sMult: 1, lVal: 0.94 },
    { key: "p6", sMult: 1, lVal: 0.96 },
    { key: "p7", sMult: 1, lVal: 0.98 },
    { key: "p8", sMult: 1, lVal: 0.99 },
  ];

  steps.forEach(({ key, sMult, lVal }) => {
    const h = baseHsl.h;
    const s = Math.min(1, Math.max(0, baseHsl.s * sMult));
    const l = Math.min(1, Math.max(0, lVal));
    const rgb = hslToRgb(h, s, l);
    palette[`--color-blue-${key}`] = rgbToHex(rgb.r, rgb.g, rgb.b);
  });

  return palette;
}

export function generateDefaultPalette() {
  return {
    "--color-blue-m4": "#05205e",
    "--color-blue-m3": "#113997",
    "--color-blue-m2": "#1e50c0",
    "--color-blue-m1": "#2759cd",
    "--color-blue-0": "#3062d4",
    "--color-blue-p1": "#6792f4",
    "--color-blue-p2": "#8db0fb",
    "--color-blue-p3": "#cdddff",
    "--color-blue-p4": "#d7e4ff",
    "--color-blue-p5": "#e5eeff",
    "--color-blue-p6": "#edf2ff",
    "--color-blue-p7": "#f5f8ff",
    "--color-blue-p8": "#fafbff",
  };
}
